import { useCallback, useEffect, useState } from "react";
import { Sparkles, Loader2, Copy, Wand2, Hash, Instagram, Video, Film, FileText, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const MODE_ICONS = {
    post_caption: Instagram,
    reels_caption: Film,
    story: Video,
    hashtags: Hash,
    proposal_draft: FileText,
};

const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

export default function AiAssistantPage() {
    const [config, setConfig] = useState(null);
    const [mode, setMode] = useState("post_caption");
    const [prompt, setPrompt] = useState("");
    const [tone, setTone] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [provider, setProvider] = useState("gemini");
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    // Free chat state
    const [tab, setTab] = useState("generate"); // generate | chat
    const [chatHistory, setChatHistory] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [chatSending, setChatSending] = useState(false);
    const [chatSession, setChatSession] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [cfg, cs] = await Promise.all([
                    api.get("/ai/config"),
                    api.get("/customers", { params: { page_size: 200 } }),
                ]);
                setConfig(cfg.data);
                setProvider(cfg.data.default_provider);
                setCustomers((cs.data.items || []).map((c) => ({ id: c.id, name: c.name })));
            } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        })();
    }, []);

    const generate = async () => {
        if (!prompt.trim()) return toast.error("Konu/istek girin.");
        setLoading(true); setResult(null);
        try {
            const { data } = await api.post("/ai/generate", { mode, prompt, tone, customer_id: customerId || undefined, provider });
            setResult(data);
            toast.success("Üretildi.");
        } catch (err) { toast.error(err?.response?.data?.detail || "Üretilemedi."); }
        finally { setLoading(false); }
    };

    const copy = (text) => {
        try {
            if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(text).then(
                    () => toast.success("Kopyalandı."),
                    () => fallbackCopy(text),
                );
            } else {
                fallbackCopy(text);
            }
        } catch { fallbackCopy(text); }
    };
    const fallbackCopy = (text) => {
        try {
            const ta = document.createElement("textarea");
            ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
            document.body.appendChild(ta); ta.select();
            document.execCommand("copy"); document.body.removeChild(ta);
            toast.success("Kopyalandı.");
        } catch { toast.error("Kopyalanamadı."); }
    };

    const sendChat = async (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;
        const userMsg = chatInput.trim();
        setChatHistory((h) => [...h, { role: "user", text: userMsg }]);
        setChatInput(""); setChatSending(true);
        try {
            const { data } = await api.post("/ai/chat", { message: userMsg, session_id: chatSession, provider });
            setChatSession(data.session_id);
            setChatHistory((h) => [...h, { role: "assistant", text: data.reply, provider: data.provider }]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Yanıt alınamadı.");
            setChatHistory((h) => [...h, { role: "assistant", text: "⚠️ Hata: " + (err?.response?.data?.detail || "AI yanıt vermedi.") }]);
        } finally { setChatSending(false); }
    };

    if (!config) return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>;

    return (
        <div className="space-y-4 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight flex items-center gap-2" data-testid="ai-title"><Sparkles className="h-6 w-6 text-[hsl(var(--brand))]" /> AI Asistan</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">İçerik üretimi, teklif taslağı ve serbest sohbet — çoklu model desteğiyle.</p>
                </div>
                <div className="inline-flex bg-muted/50 rounded-xl p-1">
                    <button onClick={() => setTab("generate")} className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold ${tab === "generate" ? "bg-background shadow-card" : "text-muted-foreground"}`} data-testid="ai-tab-generate">İçerik Üret</button>
                    <button onClick={() => setTab("chat")} className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold ${tab === "chat" ? "bg-background shadow-card" : "text-muted-foreground"}`} data-testid="ai-tab-chat">Serbest Sohbet</button>
                </div>
            </div>

            {tab === "generate" ? (
                <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
                    <div className="card-elevated p-4 h-fit space-y-3">
                        <div>
                            <label className="block text-[11.5px] font-semibold mb-1.5">Mod</label>
                            <div className="grid grid-cols-2 gap-1.5">
                                {config.modes.map((m) => {
                                    const I = MODE_ICONS[m.key] || Wand2;
                                    const active = mode === m.key;
                                    return (
                                        <button key={m.key} onClick={() => setMode(m.key)} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[11.5px] font-semibold text-left ${active ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" : "border-border hover:bg-muted"}`} data-testid={`ai-mode-${m.key}`}>
                                            <I className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{m.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-semibold mb-1.5">Model</label>
                            <select className={inputCls} value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="ai-provider-select">
                                {config.providers.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-semibold mb-1.5">Müşteri (opsiyonel)</label>
                            <select className={inputCls} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                                <option value="">— Yok —</option>
                                {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-semibold mb-1.5">Ton (opsiyonel)</label>
                            <input className={inputCls} placeholder="örn. eğlenceli, resmi, lüks, samimi" value={tone} onChange={(e) => setTone(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-semibold mb-1.5">Konu / İstek</label>
                            <textarea rows={5} placeholder="Örn: Sonbahar sezonu için deri çanta koleksiyonu tanıtımı" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-prompt-input" />
                        </div>
                        <button onClick={generate} disabled={loading} className="btn-brand w-full h-11" data-testid="ai-generate-btn">
                            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Üretiliyor…</> : <><Wand2 className="h-4 w-4" /> Üret</>}
                        </button>
                    </div>

                    <div className="space-y-3">
                        {!result && !loading && (
                            <div className="card-elevated p-10 text-center text-muted-foreground">
                                <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-40" />
                                <div className="text-[13.5px]">Sol paneldeki formu doldurup <b>Üret</b>'e tıklayın.</div>
                            </div>
                        )}
                        {loading && (
                            <div className="card-elevated p-10 text-center text-muted-foreground">
                                <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-[hsl(var(--brand))]" />
                                <div className="text-[13.5px]">AI içerik üretiyor…</div>
                            </div>
                        )}
                        {result?.variants?.map((v, i) => (
                            <div key={i} className="card-elevated p-4" data-testid={`ai-variant-${i}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <div className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-2 py-0.5 text-[11px] font-bold">Varyant {i + 1}</div>
                                    <button onClick={() => copy(v)} className="inline-flex items-center gap-1 rounded-lg bg-muted px-2.5 py-1 text-[11.5px] font-semibold hover:bg-muted/70"><Copy className="h-3 w-3" /> Kopyala</button>
                                </div>
                                <div className="whitespace-pre-wrap text-[13.5px] leading-relaxed">{v}</div>
                            </div>
                        ))}
                        {result?.hashtags?.length > 0 && (
                            <div className="card-elevated p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold"><Hash className="h-3 w-3" /> Hashtag Önerileri</div>
                                    <button onClick={() => copy(result.hashtags.map(h => "#" + h).join(" "))} className="inline-flex items-center gap-1 rounded-lg bg-muted px-2.5 py-1 text-[11.5px] font-semibold hover:bg-muted/70"><Copy className="h-3 w-3" /> Tümünü Kopyala</button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {result.hashtags.map((h, i) => (
                                        <span key={i} onClick={() => copy("#" + h)} className="cursor-pointer inline-flex items-center rounded-full bg-[hsl(var(--brand))]/8 text-[hsl(var(--brand))] px-2 py-0.5 text-[11.5px] font-semibold hover:bg-[hsl(var(--brand))]/15">#{h}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="card-elevated p-4 flex flex-col" style={{ minHeight: "600px" }}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[13px] font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4" /> AI Asistan Sohbet</div>
                        <select className={`${inputCls} max-w-[220px]`} value={provider} onChange={(e) => setProvider(e.target.value)}>
                            {config.providers.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                        </select>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                        {chatHistory.length === 0 && <div className="text-center text-muted-foreground py-12 text-[13px]">Henüz mesaj yok. Aşağıdan yazın.</div>}
                        {chatHistory.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-[13.5px] whitespace-pre-wrap ${m.role === "user" ? "bg-[hsl(var(--brand))] text-white" : "bg-muted"}`}>{m.text}</div>
                            </div>
                        ))}
                        {chatSending && <div className="flex justify-start"><div className="rounded-2xl bg-muted px-3.5 py-2.5 text-muted-foreground inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Yanıt yazılıyor…</div></div>}
                    </div>
                    <form onSubmit={sendChat} className="mt-3 flex gap-2">
                        <input placeholder="Bir soru sorun veya yardım isteyin…" className={`${inputCls} flex-1`} value={chatInput} onChange={(e) => setChatInput(e.target.value)} data-testid="ai-chat-input" />
                        <button type="submit" disabled={chatSending} className="btn-brand h-10" data-testid="ai-chat-send"><Send className="h-4 w-4" /> Gönder</button>
                    </form>
                </div>
            )}
        </div>
    );
}
